import React, { ReactElement } from 'react';
import faker from 'faker';
import { Story } from '@storybook/react';
import { ThemeProviderProps } from '@material-ui/core';
import ThemeWrap from '../components/ThemeWrap';

export { faker };

interface WithThemeProviderOptions {
  theme: ThemeProviderProps['theme'];
}

const OVERRIDE_HOOK_DATA: any = {};
const GLOBAL_HOOK_NAMES: any = {};

type CustomReturnType<T> = T extends (...args: any[]) => infer R ? R : any;
export const useMockHook = <T extends Function>(
  useHook: T,
  mockData: CustomReturnType<T>,
  namespace?: string
): T => {
  if (process.env.STORYBOOK_HOOK_MOCKS) {
    if (!useHook.name) {
      throw `Unknown function name of ${useHook}. Hooks must be named to avoid unexpected mock results!`;
    }
    if (GLOBAL_HOOK_NAMES[useHook.name]) {
      throw `${useHook.name} has already been defined, pass in a namespace to allow repeat hook names per package!`;
    }
    const name = namespace ? `${namespace}:${useHook.name}` : useHook.name;
    if (GLOBAL_HOOK_NAMES[name]) {
      throw `${name} has already been defined, rename this hook!`;
    }
    GLOBAL_HOOK_NAMES[name] = true;
    const mockReturn: any = (props: any) => {
      if (
        typeof OVERRIDE_HOOK_DATA[name] === 'boolean' &&
        !OVERRIDE_HOOK_DATA[name]
      )
        return useHook(props) as CustomReturnType<T>;
      return (OVERRIDE_HOOK_DATA[name]?.(mockData) ||
        mockData) as CustomReturnType<T>;
    };
    return mockReturn as T;
  } else return useHook;
};

export function withThemeProvider<T>(
  Component: React.ComponentType<T>,
  { theme }: WithThemeProviderOptions
) {
  const ComponentWithThemeProvider = (props: T) => {
    return (
      <ThemeWrap theme={theme}>
        <Component {...props} />
      </ThemeWrap>
    );
  };

  return ComponentWithThemeProvider;
}

export const withHookHoc = <T, H>(
  Component: React.ComponentType<T>,
  hook: (props: H) => T
) => {
  const WithHook = (props: H) => {
    const hookData = hook(props);
    return <Component {...hookData} />;
  };
  return WithHook;
};

export const _setHookRunData = (
  hookName: string,
  overrideFunction: (mock: object) => object
) => {
  if (typeof overrideFunction === 'boolean') {
    return (OVERRIDE_HOOK_DATA[hookName] = overrideFunction);
  }
  if (typeof overrideFunction === 'function') {
    return (OVERRIDE_HOOK_DATA[hookName] = overrideFunction);
  }
  throw `hook mock data for ${hookName} needs to be a function or false`;
};
export const _unsetHookRunData = (hookName: string) => {
  delete OVERRIDE_HOOK_DATA[hookName];
};

export const mockFunction: any = () => {
  /* noop */
};

export const mockAsyncFunction: any = async () => {
  /* noop */
};

export function randomEnum<T>(anEnum: T): T[keyof T] {
  const enumValues = (Object.values(anEnum) as unknown) as T[keyof T][];
  const randomIndex = Math.floor(Math.random() * enumValues.length);
  return enumValues[randomIndex];
}

export const storybookTemplate = <P,>(
  Component: (props: P) => ReactElement<unknown>
) => (props: P): Story<P> => {
  const template: Story<P> = (args) => {
    return <Component {...args} />;
  };
  const story = template.bind({});
  story.args = props;
  return story;
};
